import React, { useState } from 'react';
import { resendEmailVerification, checkEmailVerification, signOutUser } from '../firebase/auth';
import BackToMarketing from './BackToMarketing';

export default function EmailVerification({ userEmail, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const result = await resendEmailVerification();
      if (result.success) setMessage(result.message);
      else setError(result.error);
    } catch {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    setError('');
    setMessage('');
    try {
      const result = await checkEmailVerification();
      if (result.success && result.emailVerified) {
        setRedirecting(true);
        setTimeout(() => { window.location.href = '/onboarding'; }, 2000);
      } else {
        setError('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch {
      setError('Failed to check verification status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    await signOutUser();
    window.location.href = '/login';
  };

  if (redirecting) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
          <svg className="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-xl font-semibold text-white">Email verified successfully!</p>
        <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Redirecting to setup…</p>
        <div className="mt-6 w-8 h-8 border-2 border-purple-500/40 border-t-purple-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4">
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <BackToMarketing className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }} />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/screenshots/appymascot.png.png"
            alt="Appy"
            className="w-14 h-14 object-contain mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div className="hidden w-14 h-14 rounded-full mx-auto mb-4 items-center justify-center text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>A</div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Appify.AI</h1>
          <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Email Verification Required</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(192,132,252,0.1))', border: '1px solid rgba(139,92,246,0.25)' }}>
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Verify Your Email</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>We&apos;ve sent a verification link to:</p>
            <p className="text-purple-400 font-semibold mt-1 text-sm">{userEmail}</p>
          </div>

          {message && (
            <div className="rounded-xl px-4 py-3 mb-5" style={{ backgroundColor: 'rgba(34,197,94,0.08)', border: '0.5px solid rgba(34,197,94,0.25)' }}>
              <p className="text-green-400 text-sm">{message}</p>
            </div>
          )}

          {error && (
            <div className="rounded-xl px-4 py-3 mb-5" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '0.5px solid rgba(239,68,68,0.25)' }}>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleCheckVerification}
              disabled={checking}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              {checking ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Checking…
                </span>
              ) : "I've Verified My Email"}
            </button>

            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending…
                </span>
              ) : 'Resend Verification Email'}
            </button>

            <button
              onClick={handleSignOut}
              className="w-full py-2 text-sm transition"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              Sign Out
            </button>
          </div>

          {/* Instructions */}
          <div className="mt-6 rounded-xl px-4 py-4" style={{ backgroundColor: 'rgba(139,92,246,0.06)', border: '0.5px solid rgba(139,92,246,0.15)' }}>
            <div className="flex items-start gap-3">
              <svg className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <ul className="text-xs space-y-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the verification link in the email</li>
                <li>Return here and click &quot;I&apos;ve Verified My Email&quot;</li>
                <li>You&apos;ll be guided through creating your AI assistant</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
